import { Product } from '../../../../../../../../../shared/modules/evc-api/models/product';
import { TranslateService } from '@ngx-translate/core';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ChargePointType } from '../../../../../../../stores/small-biz-store.model';
import { BehaviorSubject } from 'rxjs';
import { ProductsStoreService } from '../../../../../../../stores/products-store.service';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ProductSetupMapperService } from '../../../../../../../stores/product-setup.mapper.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';

@Component({
  selector: 'evsb-choose-charging-speed',
  templateUrl: './choose-charging-speed.component.html',
  styleUrls: ['./choose-charging-speed.component.scss'],
})
export class ChooseChargingSpeedComponent implements OnChanges {
  @Input() desiredChargingStations: number;
  @Input() choice: ChargePointType;
  @Output() choiceChange = new EventEmitter<ChargePointType>();
  public chosenSpeed: string;
  public readonly desiredChargingStations$ = new BehaviorSubject<number>(null);
  public availableSpeeds$ = this.desiredChargingStations$.pipe(
    distinctUntilChanged(),
    switchMap((amount) => (amount > 1 ? this.products.proStations$ : this.products.regularStations$))
  );

  public chargePointTypes = ChargePointType;

  public get currentLang() {
    return this.translate.currentLang;
  }

  constructor(
    private products: ProductsStoreService,
    private mapper: ProductSetupMapperService,
    private translate: TranslateService
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    this.chosenSpeed = this.mapper.chargePointTypeToProductCode(this.choice);
    this.desiredChargingStations$.next(this.desiredChargingStations);
  }

  public updateSpeed(): void {
    this.choiceChange.emit(this.mapper.productCodeToChargePointType(this.chosenSpeed));
  }

  public getLabels(product: Product) {
    const location = this.mapper.productCodeToChargePointType(product.code);
    return chargingStationSpeedLabels[location];
  }
}

const chargingStationSpeedLabels: Record<
  ChargePointType,
  {
    title: string;
    img: string;
    list1: string;
    list2: string;
    list3: string;
    details1: {
      title: string;
      text: string;
    };
    details2: {
      title: string;
      text: string;
    };
    details3: {
      title: string;
      text: string;
    };
  }
> = {
  [ChargePointType.connect30]: {
    title: marker('modules.evsb.chargingStation.input.type.connect30.title'),
    img: './assets/evsb/img/30.png',
    list1: marker('modules.evsb.chargingStation.input.type.connect30.list1'),
    list2: marker('modules.evsb.chargingStation.input.type.connect30.list2'),
    list3: marker('modules.evsb.chargingStation.input.type.connect30.list3'),
    details1: {
      title: marker('modules.evsb.chargingStation.input.type.connect30.details1.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect30.details1.text'),
    },
    details2: {
      title: marker('modules.evsb.chargingStation.input.type.connect30.details2.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect30.details2.text'),
    },
    details3: {
      title: marker('modules.evsb.chargingStation.input.type.connect30.details3.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect30.details3.text'),
    },
  },
  [ChargePointType.connect30Pro]: {
    title: marker('modules.evsb.chargingStation.input.type.connect30Pro.title'),
    img: './assets/evsb/img/30.png',
    list1: marker('modules.evsb.chargingStation.input.type.connect30Pro.list1'),
    list2: marker('modules.evsb.chargingStation.input.type.connect30Pro.list2'),
    list3: marker('modules.evsb.chargingStation.input.type.connect30Pro.list3'),
    details1: {
      title: marker('modules.evsb.chargingStation.input.type.connect30Pro.details1.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect30Pro.details1.text'),
    },
    details2: {
      title: marker('modules.evsb.chargingStation.input.type.connect30Pro.details2.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect30Pro.details2.text'),
    },
    details3: {
      title: marker('modules.evsb.chargingStation.input.type.connect30Pro.details3.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect30Pro.details3.text'),
    },
  },
  [ChargePointType.connect50]: {
    title: marker('modules.evsb.chargingStation.input.type.connect50.title'),
    img: './assets/evsb/img/50.png',
    list1: marker('modules.evsb.chargingStation.input.type.connect50.list1'),
    list2: marker('modules.evsb.chargingStation.input.type.connect50.list2'),
    list3: marker('modules.evsb.chargingStation.input.type.connect50.list3'),
    details1: {
      title: marker('modules.evsb.chargingStation.input.type.connect50.details1.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect50.details1.text'),
    },
    details2: {
      title: marker('modules.evsb.chargingStation.input.type.connect50.details2.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect50.details2.text'),
    },
    details3: {
      title: marker('modules.evsb.chargingStation.input.type.connect50.details3.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect50.details3.text'),
    },
  },
  [ChargePointType.connect50Pro]: {
    title: marker('modules.evsb.chargingStation.input.type.connect50Pro.title'),
    img: './assets/evsb/img/50.png',
    list1: marker('modules.evsb.chargingStation.input.type.connect50Pro.list1'),
    list2: marker('modules.evsb.chargingStation.input.type.connect50Pro.list2'),
    list3: marker('modules.evsb.chargingStation.input.type.connect50Pro.list3'),
    details1: {
      title: marker('modules.evsb.chargingStation.input.type.connect50Pro.details1.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect50Pro.details1.text'),
    },
    details2: {
      title: marker('modules.evsb.chargingStation.input.type.connect50Pro.details2.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect50Pro.details2.text'),
    },
    details3: {
      title: marker('modules.evsb.chargingStation.input.type.connect50Pro.details3.title'),
      text: marker('modules.evsb.chargingStation.input.type.connect50Pro.details3.text'),
    },
  },
};
